import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, RefreshCw, WalletCards } from "lucide-react";
import { useSearchParams } from "react-router";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

import {
  fetchExperimentAssignments,
  fetchExperimentSummary,
} from "../experiments/experimentClient";
import { resolveMonetizationExperiment } from "./monetizationExperiments";
import {
  buildConversionKpis,
  buildRetentionKpis,
  buildSalesKpis,
  headlineMetrics,
} from "./monetizationModel";
import { MonetizationContactAudit } from "./MonetizationContactAudit";
import { MonetizationDailyActivityTable } from "./MonetizationDailyActivityTable";
import { MonetizationExperimentSelect } from "./MonetizationExperimentSelect";
import { MonetizationJourneyBoard } from "./MonetizationJourneyBoard";
import { MonetizationKpiGrid } from "./MonetizationKpiGrid";
import { MonetizationOverview } from "./MonetizationOverview";
import { MonetizationPaygateTriggers } from "./MonetizationPaygateTriggers";
import { monetizationQueryKeys } from "./monetizationQueryKeys";
import {
  AcquisitionPerformance,
  ConversionFunnel,
  PackagePerformance,
  VariantComparison,
} from "./MonetizationSummaryTables";

export function MonetizationPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get("view") === "summary" ? "summary" : "board";
  const experiment = resolveMonetizationExperiment(
    searchParams.get("experiment"),
  );
  const summary = useQuery({
    queryKey: monetizationQueryKeys.summary(experiment.id),
    queryFn: () => fetchExperimentSummary(experiment.id),
    staleTime: 30_000,
  });
  const assignments = useQuery({
    queryKey: monetizationQueryKeys.assignments(experiment.id),
    queryFn: () => fetchExperimentAssignments(experiment.id),
    staleTime: 30_000,
    enabled: view === "summary",
  });
  const headline = headlineMetrics(summary.data, experiment);
  const daily = summary.data?.daily ?? [];
  const eligibilityMismatches = experiment.requiresMetaAdEvidence
    ? (summary.data?.totals.eligibility_mismatch_contacts ?? 0)
    : 0;
  const refreshing = summary.isFetching || assignments.isFetching;
  const refresh = () =>
    Promise.all(
      [
        monetizationQueryKeys.summary(experiment.id),
        monetizationQueryKeys.assignments(experiment.id),
        monetizationQueryKeys.board(experiment.id),
      ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    );
  const setParam = (key: string, value: string | null) => {
    setSearchParams(
      (previous) => {
        const params = new URLSearchParams(previous);
        if (value) params.set(key, value);
        else params.delete(key);
        return params;
      },
      { replace: true },
    );
  };

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl space-y-1">
          <div className="flex items-center gap-2">
            <WalletCards className="size-5 text-muted-foreground" />
            <h1 className="text-2xl font-semibold">Monetization</h1>
            {experiment.live ? (
              <Badge>Live</Badge>
            ) : (
              <Badge variant="secondary">Frozen</Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {experiment.description} Revenue and package results come from
            durable Stripe-correlated events; a contact can buy again and again,
            so orders are counted per order and contacts once.
          </p>
          <code className="text-xs text-muted-foreground">{experiment.id}</code>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <MonetizationExperimentSelect
            value={experiment}
            onChange={(next) =>
              setParam(
                "experiment",
                next === resolveMonetizationExperiment(null).id ? null : next,
              )
            }
          />
          <ToggleGroup
            type="single"
            value={view}
            onValueChange={(next) =>
              next && setParam("view", next === "summary" ? "summary" : null)
            }
            variant="outline"
            size="sm"
          >
            <ToggleGroupItem value="board">Journey board</ToggleGroupItem>
            <ToggleGroupItem value="summary">Summary</ToggleGroupItem>
          </ToggleGroup>
          <Button
            variant="outline"
            onClick={() => void refresh()}
            disabled={refreshing}
          >
            <RefreshCw className={refreshing ? "animate-spin" : ""} />
            Refresh
          </Button>
        </div>
      </div>

      {summary.error || (view === "summary" && assignments.error) ? (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertTitle>Monetization data is unavailable</AlertTitle>
          <AlertDescription>
            {String(summary.error || assignments.error)}
          </AlertDescription>
        </Alert>
      ) : null}

      {summary.data?.truncated ||
      (view === "summary" && assignments.data?.truncated) ? (
        <Alert>
          <AlertTriangle />
          <AlertTitle>Partial result</AlertTitle>
          <AlertDescription>
            The experiment partition exceeded the API safety limit. Do not make
            a rollout decision from this partial result.
          </AlertDescription>
        </Alert>
      ) : null}

      {eligibilityMismatches > 0 ? (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertTitle>Rollout eligibility mismatch</AlertTitle>
          <AlertDescription>
            {eligibilityMismatches} assigned contact(s) do not have verified
            paid Meta acquisition evidence. Keep the rollout disabled and
            inspect the contact audit below.
          </AlertDescription>
        </Alert>
      ) : null}

      {summary.isPending ? (
        <p className="text-sm text-muted-foreground">
          Loading conversion results…
        </p>
      ) : headline && headline.assigned_contacts > 0 ? (
        <>
          <MonetizationOverview metrics={headline} />
          {view === "board" ? (
            <MonetizationJourneyBoard
              experimentId={experiment.id}
              variant={experiment.boardVariant}
            />
          ) : (
            <>
              <div className="grid gap-4 xl:grid-cols-3">
                <MonetizationKpiGrid
                  title="Sales"
                  items={buildSalesKpis(headline)}
                />
                <MonetizationKpiGrid
                  title="Conversion"
                  items={buildConversionKpis(headline)}
                />
                <MonetizationKpiGrid
                  title="Retention"
                  items={buildRetentionKpis(headline)}
                  footnote="Retention is read from durable facts only: a second paid order, or an Echo started after paying."
                />
              </div>
              <MonetizationPaygateTriggers
                metrics={headline}
                daily={experiment.headline === "totals" ? daily : undefined}
              />
              <MonetizationDailyActivityTable
                daily={daily}
                currency={headline.currency}
              />
              <ConversionFunnel metrics={headline} />
              <VariantComparison variants={summary.data?.variants ?? []} />
              <AcquisitionPerformance
                title={
                  experiment.requiresMetaAdEvidence
                    ? "Meta ad route performance"
                    : "Acquisition route performance"
                }
                routes={headline.acquisition_routes ?? []}
                currency={headline.currency}
              />
              <PackagePerformance offers={headline.offers ?? []} />
            </>
          )}
        </>
      ) : (
        <Alert>
          <AlertTriangle />
          <AlertTitle>No assignments yet</AlertTitle>
          <AlertDescription>
            The monetization funnel will appear once a contact is assigned to
            this paygate.
          </AlertDescription>
        </Alert>
      )}

      {view === "summary" ? (
        <MonetizationContactAudit assignments={assignments.data?.data ?? []} />
      ) : null}
    </div>
  );
}

MonetizationPage.path = "/monetization";
